import { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface AdminAsesoriaContextValue {
  selectedClienteId: number;
  setSelectedClienteId: (id: number) => void;
  activeSubTab: string;
  setActiveSubTab: (tab: string) => void;
}

const AdminAsesoriaContext = createContext<AdminAsesoriaContextValue>({
  selectedClienteId: 0,
  setSelectedClienteId: () => {},
  activeSubTab: 'planes',
  setActiveSubTab: () => {},
});

const CLIENTE_STORAGE_KEY = 'admin_asesoria_cliente_id';
const SUBTAB_STORAGE_KEY = 'admin_asesoria_subtab';

export function AdminAsesoriaProvider({ children }: { children: React.ReactNode }) {
  const [selectedClienteId, setId] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(CLIENTE_STORAGE_KEY);
      const parsed = raw ? parseInt(raw, 10) : 0;
      return Number.isNaN(parsed) ? 0 : parsed;
    } catch {
      return 0;
    }
  });

  const [activeSubTab, setTab] = useState<string>(() => {
    try {
      return localStorage.getItem(SUBTAB_STORAGE_KEY) || 'planes';
    } catch {
      return 'planes';
    }
  });

  const setSelectedClienteId = useCallback((id: number) => {
    try {
      if (id > 0) {
        localStorage.setItem(CLIENTE_STORAGE_KEY, String(id));
      } else {
        localStorage.removeItem(CLIENTE_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
    setId(id);
  }, []);

  const setActiveSubTab = useCallback((tab: string) => {
    try {
      localStorage.setItem(SUBTAB_STORAGE_KEY, tab);
    } catch {
      // ignore
    }
    setTab(tab);
  }, []);

  // Listen to storage changes from other tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === CLIENTE_STORAGE_KEY) {
        const parsed = e.newValue ? parseInt(e.newValue, 10) : 0;
        setId(Number.isNaN(parsed) ? 0 : parsed);
      }
      if (e.key === SUBTAB_STORAGE_KEY && e.newValue) {
        setTab(e.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return (
    <AdminAsesoriaContext.Provider value={{ selectedClienteId, setSelectedClienteId, activeSubTab, setActiveSubTab }}>
      {children}
    </AdminAsesoriaContext.Provider>
  );
}

export function useAdminAsesoriaContext() {
  return useContext(AdminAsesoriaContext);
}